'use client';

import React, { useEffect, useState } from 'react';
import {
    Star,
    CheckCircle2,
    Quote as QuoteIcon,
    ChevronLeft,
    ChevronRight,
    MessageSquareQuote
} from 'lucide-react';
import { ITESTIMONIALS } from '@/utils/interface';
import { apiRoutes } from '@/utils/api/routes';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

interface ProfessionalTestimonialsProps {
    testimonials?: ITESTIMONIALS[];
    professionalId?: string;
    professionalName?: string;
}

const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
    }).format(d);
};

const getInitials = (name?: string): string => {
    if (!name) return 'U';
    const parts = name?.trim()?.split(' ')?.filter(Boolean) || [];
    if (parts?.length === 1) return parts?.[0]?.substring(0, 2)?.toUpperCase() || 'U';
    if (parts?.length > 1) {
        const first = parts?.[0]?.[0] || '';
        const last = parts?.[parts.length - 1]?.[0] || '';
        return (first + last)?.toUpperCase() || 'U';
    }
    return 'U';
};

export function ProfessionalTestimonials({
    testimonials: initialTestimonials,
    professionalId,
    professionalName,
}: ProfessionalTestimonialsProps) {
    const [testimonials, setTestimonials] = useState<ITESTIMONIALS[]>(initialTestimonials || []);
    const [loading, setLoading] = useState<boolean>(!initialTestimonials?.length && Boolean(professionalId));
    const [api, setApi] = useState<CarouselApi>();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (initialTestimonials?.length) {
            setTestimonials(initialTestimonials);
            setLoading(false);
            return;
        }

        if (!professionalId) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        const fetchTestimonials = async () => {
            try {
                setLoading(true);
                const res = await fetch(apiRoutes?.clientRoutes?.testimonials, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ professional: professionalId }),
                });

                if (!res?.ok) throw new Error('Failed to fetch testimonials');
                const json = await res?.json();
                const fetched = (json?.data?.docs || json?.data?.data || json?.data || []) as ITESTIMONIALS[];
                if (isMounted) {
                    setTestimonials(Array.isArray(fetched) ? fetched : []);
                }
            } catch (error) {
                console.error('Error fetching professional testimonials:', error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchTestimonials();

        return () => {
            isMounted = false;
        };
    }, [professionalId, initialTestimonials]);

    useEffect(() => {
        if (!api) return;

        const updateScrollState = () => {
            setCanScrollPrev(Boolean(api?.canScrollPrev?.()));
            setCanScrollNext(Boolean(api?.canScrollNext?.()));
        };

        updateScrollState();
        api?.on('select', updateScrollState);
        api?.on('reInit', updateScrollState);

        return () => {
            api?.off('select', updateScrollState);
            api?.off('reInit', updateScrollState);
        };
    }, [api]);

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => ({ ...prev, [id]: !prev?.[id] }));
    };

    if (loading) {
        return (
            <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                    <div className="h-6 w-48 bg-blue-100/60 rounded-md animate-pulse" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-56 bg-white rounded-2xl border border-blue-100/80 p-5 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (!testimonials || testimonials?.length === 0) {
        return null;
    }

    // Calculate rating score if present
    const ratingsWithValues = testimonials
        ?.map((t) => t?.rating || t?.ratingData?.average)
        ?.filter((r): r is number => typeof r === 'number' && r > 0) || [];
    const avgRating = ratingsWithValues?.length
        ? (ratingsWithValues?.reduce((a, b) => a + b, 0) / ratingsWithValues?.length)?.toFixed(1)
        : null;

    const renderTestimonialCard = (item: ITESTIMONIALS, index: number) => {
        const id = item?._id || String(index);
        const formattedDate = formatDate(item?.createdAt);
        const isExpanded = Boolean(expandedIds?.[id]);
        const message = item?.message || '';
        const shouldTruncate = (message?.length || 0) > 200;
        const displayText = shouldTruncate && !isExpanded ? `${message?.slice(0, 190)}...` : message;

        const subtitle = [item?.designation, item?.companyName]?.filter(Boolean)?.join(' at ');

        return (
            <div
                key={id}
                className="group relative bg-white rounded-2xl p-5 sm:p-6 border border-blue-100/80 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between h-full overflow-hidden"
            >
                {/* Subtle gradient glow in top corner */}
                <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-bl from-blue-50/80 via-transparent to-transparent pointer-events-none rounded-tr-2xl" />

                {/* Card Top: Stars & Quote Icon */}
                <div>
                    <div className="flex items-center justify-between mb-3.5">
                        {item?.rating ? (
                            <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                        key={star}
                                        className={`w-4 h-4 ${star <= Math.round(item?.rating || 0)
                                            ? 'fill-amber-400 text-amber-400'
                                            : 'fill-gray-200 text-gray-200'
                                            }`}
                                    />
                                ))}
                                <span className="text-xs font-bold text-gray-700 ml-1.5">{item?.rating}</span>
                            </div>
                        ) : (
                            <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[11px] font-semibold">
                                <CheckCircle2 className="w-3 h-3 text-blue-600" />
                                <span>Endorsement</span>
                            </div>
                        )}

                        <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                            <QuoteIcon className="w-4 h-4 text-blue-600" />
                        </div>
                    </div>

                    {/* Testimonial Quote Message */}
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed italic relative z-10">
                        &ldquo;{displayText}&rdquo;
                    </p>

                    {shouldTruncate && (
                        <button
                            type="button"
                            onClick={() => toggleExpand(id)}
                            className="text-xs font-semibold text-blue-600 hover:text-blue-700 mt-2 inline-block transition-colors"
                        >
                            {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                    )}
                </div>

                {/* Card Bottom: Reviewer Profile */}
                <div className="pt-5 mt-5 border-t border-gray-100 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-xs">
                        {getInitials(item?.name)}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                            <h4 className="text-xs sm:text-sm font-bold text-gray-900 truncate">
                                {item?.name || 'Client'}
                            </h4>
                            {item?.isVerified !== false && (
                                <CheckCircle2 className="w-3.5 h-3.5 fill-blue-600 text-white shrink-0" />
                            )}
                        </div>

                        {subtitle && (
                            <p className="text-[11px] sm:text-xs text-gray-600 truncate mt-0.5 font-medium">
                                {subtitle}
                            </p>
                        )}

                        {formattedDate && (
                            <span className="text-[10px] text-gray-400 block mt-0.5">
                                {formattedDate}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section id="testimonials-section" className="space-y-4 pt-2">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-200/80 text-blue-700 text-[11px] font-semibold mb-1">
                        <MessageSquareQuote className="w-3 h-3 text-blue-600" />
                        <span>Client Endorsements</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <h3 className="text-lg font-bold text-gray-900">
                            Testimonials & Recommendations
                        </h3>
                        {avgRating && (
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold">
                                <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                                <span>{avgRating}</span>
                                <span className="text-gray-400 font-normal">({testimonials?.length})</span>
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Verified feedback from clients and collaborators who worked with {professionalName || 'this professional'}.
                    </p>
                </div>

                {/* Carousel Navigation Buttons */}
                {(testimonials?.length || 0) > 3 && (
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                        <button
                            type="button"
                            onClick={() => api?.scrollPrev?.()}
                            disabled={!canScrollPrev}
                            aria-label="Previous testimonial"
                            className="w-8 h-8 rounded-lg border border-blue-200 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-blue-600 transition-colors shadow-2xs"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => api?.scrollNext?.()}
                            disabled={!canScrollNext}
                            aria-label="Next testimonial"
                            className="w-8 h-8 rounded-lg border border-blue-200 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-blue-600 transition-colors shadow-2xs"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Testimonials Display (Grid or Carousel) */}
            {(testimonials?.length || 0) <= 3 ? (
                <div className={`grid grid-cols-1 ${testimonials?.length === 2 ? 'sm:grid-cols-2' : testimonials?.length === 1 ? 'sm:grid-cols-1 max-w-xl' : 'sm:grid-cols-2 lg:grid-cols-3'} gap-4`}>
                    {testimonials?.map((item, index) => renderTestimonialCard(item, index))}
                </div>
            ) : (
                <Carousel
                    setApi={setApi}
                    opts={{ align: 'start', loop: true }}
                    plugins={[Autoplay({ delay: 4500, stopOnInteraction: true })]}
                    className="w-full"
                >
                    <CarouselContent className="-ml-4">
                        {testimonials?.map((item, index) => (
                            <CarouselItem key={item?._id || index} className="pl-4 sm:basis-1/2 lg:basis-1/3">
                                {renderTestimonialCard(item, index)}
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            )}
        </section>
    );
}

export default ProfessionalTestimonials;
